import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc, 
  getDocs,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { validateStaffStatus } from './statusValidation';

/**
 * Incident management utilities with inheritance bug fixes
 * Implements clean creation and workflow guardrails from GPT's requirements
 */

/**
 * Create incident with blank staff slate (no inheritance)
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID  
 * @param {Object} incidentData - Incident data (excluding staff)
 * @returns {Promise<string>} Created incident ID
 */
export const createIncidentWithBlankStaff = async (db, orgId, incidentData) => {
  console.log(`🏗️ Creating new incident with blank staff for org ${orgId}`);
  
  try {
    const incidentRef = doc(collection(db, 'c3_incidents'));
    
    // Clean incident data - explicitly exclude any staff-related fields
    const cleanIncidentData = {
      ...incidentData,
      status: 'Active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Explicitly ensure no staff inheritance
      staffAssignments: undefined, // Remove if accidentally included
      staffStatus: undefined,       // Remove if accidentally included
      totalStaff: undefined         // Remove if accidentally included
    };
    
    // Remove undefined fields
    Object.keys(cleanIncidentData).forEach(key => {
      if (cleanIncidentData[key] === undefined) {
        delete cleanIncidentData[key];
      }
    });
    
    await setDoc(incidentRef, cleanIncidentData);
    
    console.log(`✅ Created incident ${incidentRef.id} with clean staff slate`);
    return incidentRef.id;
    
  } catch (error) {
    console.error(`❌ Failed to create incident:`, error);
    throw error;
  }
};

/**
 * Close incident with validation and guardrails
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @param {Object} options - Close options
 * @returns {Promise<Object>} Close results with warnings
 */
export const closeIncidentWithValidation = async (db, orgId, incidentId, options = {}) => {
  const { userConfirmation = false, force = false } = options;
  
  console.log(`🔐 Attempting to close incident ${incidentId}`);
  
  try {
    // Check current staff status before closing
    const staffQuery = query(
      collection(db, `orgs/${orgId}/incidents/${incidentId}/staffAssignments`)
    );
    const staffSnapshot = await getDocs(staffQuery);
    
    // Analyze staff status
    const staffAnalysis = {
      total: staffSnapshot.docs.length,
      unaccounted: 0,
      need_assistance: 0,
      hospitalised: 0,
      deceased: 0
    };
    
    staffSnapshot.docs.forEach(doc => {
      const status = validateStaffStatus(doc.data().status);
      if (status === 'unaccounted') staffAnalysis.unaccounted++;
      if (status === 'need_assistance') staffAnalysis.need_assistance++;
      if (status === 'hospitalised') staffAnalysis.hospitalised++;
      if (status === 'deceased') staffAnalysis.deceased++;
    });
    
    // Generate warnings for concerning staff statuses
    const warnings = [];
    if (staffAnalysis.unaccounted > 0) {
      warnings.push(`${staffAnalysis.unaccounted} staff still unaccounted for`);
    }
    if (staffAnalysis.need_assistance > 0) {
      warnings.push(`${staffAnalysis.need_assistance} staff need assistance`);
    }
    if (staffAnalysis.hospitalised > 0) {
      warnings.push(`${staffAnalysis.hospitalised} staff hospitalised`);
    }
    
    // Check if confirmation is required
    const requiresConfirmation = warnings.length > 0 && !userConfirmation && !force;
    
    if (requiresConfirmation) {
      return {
        requiresConfirmation: true,
        warnings,
        staffAnalysis,
        message: `Warning: ${warnings.join(', ')}. Closing will remove this incident from dashboard totals. Continue?`
      };
    }
    
    // Proceed with closing
    await updateDoc(doc(db, `orgs/${orgId}/incidents/${incidentId}`), {
      status: 'closed',
      closedAt: serverTimestamp(),
      staffAnalysisAtClose: staffAnalysis,
      warningsAtClose: warnings
    });
    
    console.log(`✅ Successfully closed incident ${incidentId}`);
    
    return {
      success: true,
      warnings,
      staffAnalysis,
      message: `Incident closed. ${staffAnalysis.total} staff records archived.`
    };
    
  } catch (error) {
    console.error(`❌ Failed to close incident ${incidentId}:`, error);
    throw error;
  }
};

/**
 * Reopen closed incident
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @returns {Promise<Object>} Reopen results
 */
export const reopenIncident = async (db, orgId, incidentId) => {
  console.log(`🔓 Reopening incident ${incidentId}`);
  
  try {
    await updateDoc(doc(db, `orgs/${orgId}/incidents/${incidentId}`), {
      status: 'Active',
      reopenedAt: serverTimestamp(),
      closedAt: null
    });
    
    console.log(`✅ Successfully reopened incident ${incidentId}`);
    
    return {
      success: true,
      message: 'Incident reopened and will appear in dashboard totals again.'
    };
    
  } catch (error) {
    console.error(`❌ Failed to reopen incident ${incidentId}:`, error);
    throw error;
  }
};

/**
 * Set staff status for incident with validation
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @param {string} userId - User/Staff ID
 * @param {string} status - Staff status
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Object>} Update results
 */
export const setIncidentStaffStatusWithValidation = async (
  db, 
  orgId, 
  incidentId, 
  userId, 
  status, 
  metadata = {}
) => {
  console.log(`👤 Setting staff status for ${userId} in incident ${incidentId}: ${status}`);
  
  try {
    const validatedStatus = validateStaffStatus(status);
    
    if (validatedStatus !== status) {
      console.warn(`⚠️ Status '${status}' invalid, using '${validatedStatus}' instead`);
    }
    
    const staffRef = doc(db, `orgs/${orgId}/incidents/${incidentId}/staffAssignments/${userId}`);
    
    const staffData = {
      status: validatedStatus,
      role: metadata.role || null,
      notes: metadata.notes || null,
      fullName: metadata.fullName || null,
      identity: metadata.identity || null,
      updatedAt: serverTimestamp(),
      updatedBy: metadata.updatedBy || null
    };
    
    // Only set createdAt if this is a new record
    if (metadata.isNewRecord) {
      staffData.createdAt = serverTimestamp();
      staffData.source = metadata.source || 'manual';
    }
    
    await setDoc(staffRef, staffData, { merge: true });
    
    console.log(`✅ Successfully updated staff status for ${userId}`);
    
    return {
      success: true,
      status: validatedStatus,
      originalStatus: status,
      wasValidated: validatedStatus !== status
    };
    
  } catch (error) {
    console.error(`❌ Failed to set staff status for ${userId}:`, error);
    throw error;
  }
};

/**
 * Get staff assignments for incident
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @returns {Promise<Array>} Staff assignments array
 */
export const getIncidentStaff = async (db, orgId, incidentId) => {
  try {
    const staffQuery = query(
      collection(db, `orgs/${orgId}/incidents/${incidentId}/staffAssignments`)
    );
    const snapshot = await getDocs(staffQuery);
    
    const staffList = snapshot.docs.map(doc => ({
      userId: doc.id,
      ...doc.data(),
      status: validateStaffStatus(doc.data().status)
    }));
    
    console.log(`📋 Retrieved ${staffList.length} staff assignments for incident ${incidentId}`);
    
    return staffList;
    
  } catch (error) {
    console.error(`❌ Failed to get staff for incident ${incidentId}:`, error);
    throw error;
  }
};

/**
 * Remove staff assignment from incident
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @param {string} userId - User/Staff ID to remove
 * @returns {Promise<Object>} Removal results
 */
export const removeIncidentStaff = async (db, orgId, incidentId, userId) => {
  console.log(`🗑️ Removing staff ${userId} from incident ${incidentId}`);
  
  try {
    const staffRef = doc(db, `orgs/${orgId}/incidents/${incidentId}/staffAssignments/${userId}`);
    
    // Soft delete by marking as removed
    await updateDoc(staffRef, {
      status: 'removed',
      removedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log(`✅ Successfully removed staff ${userId}`);
    
    return {
      success: true,
      message: `Staff member removed from incident`
    };
    
  } catch (error) {
    console.error(`❌ Failed to remove staff ${userId}:`, error);
    throw error;
  }
};

/**
 * Bulk update staff statuses for incident
 * @param {Object} db - Firestore database instance
 * @param {string} orgId - Organization ID
 * @param {string} incidentId - Incident ID
 * @param {Array} updates - Array of {userId, status, metadata} objects
 * @returns {Promise<Object>} Bulk update results
 */
export const bulkUpdateIncidentStaff = async (db, orgId, incidentId, updates) => {
  console.log(`📊 Bulk updating ${updates.length} staff assignments for incident ${incidentId}`);
  
  try {
    const results = {
      successful: 0,
      failed: 0,
      errors: []
    };
    
    // Process updates sequentially to avoid overwhelming Firestore
    for (const update of updates) {
      try {
        await setIncidentStaffStatusWithValidation(
          db,
          orgId,
          incidentId,
          update.userId,
          update.status,
          update.metadata || {}
        );
        results.successful++;
      } catch (error) {
        results.failed++;
        results.errors.push({
          userId: update.userId,
          error: error.message
        });
      }
    }
    
    console.log(`✅ Bulk update complete: ${results.successful} successful, ${results.failed} failed`);
    
    return results;
    
  } catch (error) {
    console.error(`❌ Bulk update failed for incident ${incidentId}:`, error);
    throw error;
  }
};
